import { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	TextField,
} from "@mui/material";
import type {
	CatalogOption,
	PurchaseOrderDetail,
	RollBlankOption,
	UpsertPurchaseOrderDetailParams,
} from "../models/PurchaseOrders";

interface Props {
	open: boolean;
	purchaseOrderId: number;
	item: PurchaseOrderDetail | null;
	rollBlanks: RollBlankOption[];
	suppliers: CatalogOption[];
	costCenters: CatalogOption[];
	isSaving: boolean;
	onClose: () => void;
	onSave: (params: UpsertPurchaseOrderDetailParams) => void;
}

interface FormState {
	RollBlankId: number | null;
	RollBlankDiameter: string;
	QuantityOrdered: string;
	RollCost: string;
	SupplierId: number | null;
	CostCenterId: number | null;
	Active: boolean;
}

const EMPTY: FormState = {
	RollBlankId: null,
	RollBlankDiameter: "",
	QuantityOrdered: "",
	RollCost: "",
	SupplierId: null,
	CostCenterId: null,
	Active: true,
};

const numOrNull = (value: string): number | null => {
	const trimmed = value.trim();
	if (trimmed === "") return null;
	const n = Number(trimmed);
	return Number.isNaN(n) ? null : n;
};

export const PurchaseOrderItemModal = ({
	open,
	purchaseOrderId,
	item,
	rollBlanks,
	suppliers,
	costCenters,
	isSaving,
	onClose,
	onSave,
}: Props) => {
	const [form, setForm] = useState<FormState>(EMPTY);

	useEffect(() => {
		if (!open) return;
		if (item) {
			setForm({
				RollBlankId: item.RollBlankId ?? null,
				RollBlankDiameter:
					item.RollBlankDiameter != null ? String(item.RollBlankDiameter) : "",
				QuantityOrdered:
					item.QuantityOrdered != null ? String(item.QuantityOrdered) : "",
				RollCost: item.RollCost != null ? String(item.RollCost) : "",
				SupplierId: item.SupplierId ?? null,
				CostCenterId: item.CostCenterId ?? null,
				Active: item.Active ?? true,
			});
		} else {
			setForm(EMPTY);
		}
	}, [open, item]);

	const selectedBlank = useMemo(
		() => rollBlanks.find((b) => b.Id === form.RollBlankId) ?? null,
		[rollBlanks, form.RollBlankId],
	);
	const selectedSupplier = useMemo(
		() => suppliers.find((s) => s.Id === form.SupplierId) ?? null,
		[suppliers, form.SupplierId],
	);
	const selectedCostCenter = useMemo(
		() => costCenters.find((c) => c.Id === form.CostCenterId) ?? null,
		[costCenters, form.CostCenterId],
	);

	const diameter = numOrNull(form.RollBlankDiameter);
	// Quantity drives the receive slots in Rolls Received — without it the item
	// can never receive a roll, so it is required here (legacy allowed saving
	// without it, leaving a dead item).
	const quantity = numOrNull(form.QuantityOrdered);
	const canSave =
		form.RollBlankId != null &&
		diameter != null &&
		diameter > 0 &&
		quantity != null &&
		quantity >= 1 &&
		!isSaving;

	const handleSave = () => {
		onSave({
			Id: item?.Id ?? null,
			Active: form.Active,
			PurchaseOrderId: purchaseOrderId,
			RollBlankId: form.RollBlankId,
			RollBlankDiameter: diameter,
			QuantityOrdered: numOrNull(form.QuantityOrdered),
			RollCost: numOrNull(form.RollCost),
			SupplierId: form.SupplierId,
			CostCenterId: form.CostCenterId,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{item ? "Edit Purchase Order Item" : "New Purchase Order Item"}
			</DialogTitle>
			<DialogContent dividers>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<Autocomplete
						size="small"
						options={rollBlanks}
						value={selectedBlank}
						getOptionLabel={(o) => o.Dwg}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						onChange={(_e, option) =>
							setForm({
								...form,
								RollBlankId: option?.Id ?? null,
								// Pre-fill the blank diameter from the selected blank's
								// new diameter when adding a new item.
								RollBlankDiameter:
									!item && option?.NewDiameter != null
										? String(option.NewDiameter)
										: form.RollBlankDiameter,
							})
						}
						renderInput={(params) => (
							<TextField {...params} label="Roll Blank" required />
						)}
					/>
					<TextField
						label="Blank Diameter"
						type="number"
						size="small"
						required
						value={form.RollBlankDiameter}
						onChange={(e) =>
							setForm({ ...form, RollBlankDiameter: e.target.value })
						}
					/>
					<TextField
						label="Quantity Ordered"
						type="number"
						size="small"
						required
						value={form.QuantityOrdered}
						onChange={(e) =>
							setForm({ ...form, QuantityOrdered: e.target.value })
						}
					/>
					<TextField
						label="Roll Cost"
						type="number"
						size="small"
						value={form.RollCost}
						onChange={(e) => setForm({ ...form, RollCost: e.target.value })}
					/>
					<Autocomplete
						size="small"
						options={suppliers}
						value={selectedSupplier}
						getOptionLabel={(o) => o.Name}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						onChange={(_e, option) =>
							setForm({ ...form, SupplierId: option?.Id ?? null })
						}
						renderInput={(params) => <TextField {...params} label="Supplier" />}
					/>
					<Autocomplete
						size="small"
						options={costCenters}
						value={selectedCostCenter}
						getOptionLabel={(o) => o.Name}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						onChange={(_e, option) =>
							setForm({ ...form, CostCenterId: option?.Id ?? null })
						}
						renderInput={(params) => (
							<TextField {...params} label="Cost Center" />
						)}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={form.Active}
								onChange={(e) => setForm({ ...form, Active: e.target.checked })}
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} disabled={!canSave}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
